import type {
  AreaAdminView,
  AreaCreateInput,
  CableTypeInput,
  CableTypeView,
  FiberColorView,
  MasterData,
  MonitoringDefaults,
  SplitterView,
} from '@ftthnms/shared';
import { Badge, Button, RegionChip } from '@ftthnms/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, Map as MapIcon, Pencil, Plus, Save, SlidersHorizontal, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field, FormDialog, Switch, Tabs } from '../components/common';
import { EmptyState } from '@ftthnms/ui';
import { useToast } from '../components/Toast';
import { api, nf, nf1, rupiah, send } from '../lib/api';
import { useSession } from '../lib/session';

type Tab = 'areas' | 'types' | 'splitters' | 'colors' | 'cables' | 'monitoring';

/** Pengaturan (PRD §5.10): areas and monitoring defaults (Admin), master data (Admin, Planner). */
export function SettingsPage() {
  const { ability } = useSession();
  const admin = ability.can('manage', 'Settings');
  const master = ability.can('manage', 'MasterData');
  const tabs = [
    ...(admin ? ([['areas', 'Wilayah']] as const) : []),
    ...(master
      ? ([
          ['types', 'Jenis aset'],
          ['splitters', 'Splitter'],
          ['colors', 'Warna core'],
          ['cables', 'Tipe kabel'],
        ] as const)
      : []),
    ...(admin ? ([['monitoring', 'Monitoring']] as const) : []),
  ];
  const [tab, setTab] = useState<Tab>(tabs[0]?.[0] ?? 'areas');

  if (tabs.length === 0) {
    return (
      <div className="fn-page-body">
        <EmptyState
          icon={<SlidersHorizontal className="i i--xl" aria-hidden />}
          title="Tidak punya akses"
          text="Pengaturan hanya untuk Admin dan Planner."
        />
      </div>
    );
  }
  return (
    <div className="fn-page-body">
      <Tabs<Tab> tabs={tabs} value={tab} onChange={setTab} />
      {tab === 'areas' && <AreasTab />}
      {tab === 'types' && <AssetTypesTab />}
      {tab === 'splitters' && <SplittersTab />}
      {tab === 'colors' && <ColorsTab />}
      {tab === 'cables' && <CablesTab />}
      {tab === 'monitoring' && <MonitoringTab />}
    </div>
  );
}

function useMaster() {
  return useQuery({ queryKey: ['master', 'all'], queryFn: () => api<MasterData>('/api/v1/master') });
}

function useSaveMaster() {
  const qc = useQueryClient();
  return (data: MasterData) => {
    qc.setQueryData(['master', 'all'], data);
    void qc.invalidateQueries({ queryKey: ['master', 'cable-types'] });
  };
}

// ------------------------------------------------------------------ areas

function AreasTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const areas = useQuery({
    queryKey: ['settings', 'areas'],
    queryFn: () => api<AreaAdminView[]>('/api/v1/settings/areas'),
  });
  const [editing, setEditing] = useState<AreaAdminView | 'new' | null>(null);
  const [removing, setRemoving] = useState<AreaAdminView | null>(null);

  const refresh = async (list: AreaAdminView[]) => {
    qc.setQueryData(['settings', 'areas'], list);
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['area-directory'] }),
      qc.invalidateQueries({ queryKey: ['areas'] }),
      qc.invalidateQueries({ queryKey: ['me'] }),
    ]);
  };

  return (
    <section className="fn-card" style={{ overflow: 'hidden' }}>
      <div className="fn-card__header">
        <div>
          <h3 className="fn-card__title">Wilayah</h3>
          <p className="fn-card__desc">
            Kode wilayah = roleCode teknisi di Speedmax ID. Menambah wilayah otomatis membuat pemetaan role
            teknisinya; buat role dengan kode yang sama di Speedmax ID.
          </p>
        </div>
        <Button
          size="sm"
          variant="primary"
          icon={<Plus className="i" aria-hidden />}
          onClick={() => setEditing('new')}
        >
          Tambah wilayah
        </Button>
      </div>
      <div className="fn-table-wrap">
        <table className="fn-table">
          <thead>
            <tr>
              <th>Wilayah</th>
              <th>Kode</th>
              <th>Prefix kode aset</th>
              <th className="is-num">Aset</th>
              <th className="is-num">Jalur</th>
              <th className="is-num">Teknisi</th>
              <th>Batas di peta</th>
              <th className="is-end">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {areas.data?.map((a) => (
              <tr key={a.code}>
                <td>
                  <RegionChip code={a.code} label={a.name} />
                </td>
                <td className="mono">
                  {a.code}
                  {!a.hasSsoMapping && (
                    <span style={{ marginLeft: 6 }}>
                      <Badge tone="warning">tanpa role SSO</Badge>
                    </span>
                  )}
                </td>
                <td className="mono">{a.prefix}</td>
                <td className="is-num">{nf.format(a.assetCount)}</td>
                <td className="is-num">{nf.format(a.routeCount)}</td>
                <td className="is-num">{nf.format(a.userCount)}</td>
                <td>
                  {a.hasBoundary ? (
                    <Badge tone="success" dot>
                      Sudah digambar
                    </Badge>
                  ) : (
                    <Link to="/map" className="fn-link" title="Buka Peta → panel Batas wilayah → ikon pensil">
                      <MapIcon
                        className="i i--sm"
                        aria-hidden
                        style={{ display: 'inline', verticalAlign: '-2px' }}
                      />{' '}
                      Gambar di peta
                    </Link>
                  )}
                </td>
                <td className="is-end">
                  <div className="fn-table__actions">
                    <button
                      type="button"
                      className="fn-icon-btn fn-icon-btn--sm"
                      aria-label={`Ubah ${a.name}`}
                      onClick={() => setEditing(a)}
                    >
                      <Pencil className="i i--sm" aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="fn-icon-btn fn-icon-btn--sm fn-icon-btn--danger"
                      aria-label={`Hapus ${a.name}`}
                      disabled={a.assetCount > 0 || a.routeCount > 0}
                      title={
                        a.assetCount > 0 || a.routeCount > 0
                          ? 'Masih ada aset atau jalur di wilayah ini'
                          : undefined
                      }
                      onClick={() => setRemoving(a)}
                    >
                      <Trash2 className="i i--sm" aria-hidden />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <AreaDialog
          area={editing === 'new' ? null : editing}
          onSaved={refresh}
          onClose={() => setEditing(null)}
        />
      )}
      {removing && (
        <ConfirmDialog
          title={`Hapus wilayah ${removing.name}?`}
          icon={<Trash2 className="i" aria-hidden />}
          confirmLabel="Hapus"
          onConfirm={async () => {
            await refresh(await send<AreaAdminView[]>('DELETE', `/api/v1/settings/areas/${removing.code}`));
            toast({ tone: 'success', text: `Wilayah ${removing.name} dihapus.` });
            setRemoving(null);
          }}
          onClose={() => setRemoving(null)}
        >
          <p>
            Pemetaan role teknisi {removing.code} ikut dihapus
            {removing.userCount > 0 ? `, dan ${removing.userCount} teknisi kehilangan akses wilayah ini` : ''}
            .
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

function AreaDialog({
  area,
  onSaved,
  onClose,
}: {
  area: AreaAdminView | null;
  onSaved: (list: AreaAdminView[]) => Promise<void>;
  onClose: () => void;
}) {
  const toast = useToast();
  const [v, setV] = useState<AreaCreateInput>({
    code: area?.code ?? '',
    name: area?.name ?? '',
    prefix: area?.prefix ?? '',
    color: area?.color ?? '#38bdf8',
  });
  return (
    <FormDialog
      title={area ? `Ubah wilayah ${area.name}` : 'Tambah wilayah'}
      onClose={onClose}
      onSubmit={async () => {
        const list = area
          ? await send<AreaAdminView[]>('PUT', `/api/v1/settings/areas/${area.code}`, {
              name: v.name,
              prefix: v.prefix,
              color: v.color,
            })
          : await send<AreaAdminView[]>('POST', '/api/v1/settings/areas', v);
        await onSaved(list);
        toast({
          tone: 'success',
          text: area
            ? `Wilayah ${v.name} tersimpan.`
            : `Wilayah ${v.name} ditambahkan. Buat role "${v.code.toLowerCase()}" di Speedmax ID untuk teknisinya.`,
        });
      }}
    >
      {(fe) => (
        <div className="fn-form-grid">
          <Field label="Nama" required wide error={fe.name}>
            <input
              className="fn-input"
              value={v.name}
              onChange={(e) => setV({ ...v, name: e.target.value })}
              required
            />
          </Field>
          <Field
            label="Kode (roleCode SSO)"
            required
            error={fe.code}
            hint={area ? 'Kode tidak bisa diubah' : 'mis. cibeber'}
          >
            <input
              className="fn-input fn-input--mono"
              value={v.code}
              disabled={!!area}
              onChange={(e) => setV({ ...v, code: e.target.value })}
              required
            />
          </Field>
          <Field
            label="Prefix kode aset"
            required
            error={fe.prefix}
            hint={area ? 'Kode aset lama tidak berubah' : 'mis. CBR → ODP-CBR-0001'}
          >
            <input
              className="fn-input fn-input--mono"
              value={v.prefix}
              maxLength={6}
              onChange={(e) => setV({ ...v, prefix: e.target.value.toUpperCase() })}
              required
            />
          </Field>
          <Field label="Warna" error={fe.color}>
            <input
              className="fn-input"
              type="color"
              value={v.color}
              onChange={(e) => setV({ ...v, color: e.target.value })}
              style={{ height: 38, padding: 4 }}
            />
          </Field>
        </div>
      )}
    </FormDialog>
  );
}

// ------------------------------------------------------------------ asset types

function AssetTypesTab() {
  const master = useMaster();
  const save = useSaveMaster();
  const toast = useToast();
  return (
    <section className="fn-card" style={{ overflow: 'hidden' }}>
      <div className="fn-card__header">
        <div>
          <h3 className="fn-card__title">Jenis aset</h3>
          <p className="fn-card__desc">
            Kode jenis tetap (dipakai import/export). "Dipantau otomatis" berlaku untuk aset yang punya IP dan
            belum punya pengaturan monitoring sendiri.
          </p>
        </div>
      </div>
      <div className="fn-table-wrap">
        <table className="fn-table">
          <thead>
            <tr>
              <th>Jenis</th>
              <th>Kode</th>
              <th>Lapisan</th>
              <th className="is-num">Jumlah aset</th>
              <th className="is-end">Dipantau otomatis</th>
            </tr>
          </thead>
          <tbody>
            {master.data?.assetTypes.map((t) => (
              <tr key={t.code}>
                <td>{t.name}</td>
                <td className="mono">{t.code}</td>
                <td>{LAYER_LABELS[t.layer] ?? t.layer}</td>
                <td className="is-num">{nf.format(t.assetCount)}</td>
                <td className="is-end">
                  {t.supportsMonitoring ? (
                    <Switch
                      label={`Pantau ${t.name} otomatis`}
                      checked={t.monitorByDefault}
                      onChange={(on) =>
                        void send<MasterData>('PUT', `/api/v1/master/asset-types/${t.code}`, {
                          monitorByDefault: on,
                        })
                          .then((d) => {
                            save(d);
                            toast({
                              tone: 'success',
                              text: `${t.name} ${on ? 'dipantau' : 'tidak dipantau'} otomatis.`,
                            });
                          })
                          .catch((e: Error) => toast({ tone: 'danger', text: e.message }))
                      }
                    />
                  ) : (
                    <span className="fn-hint">pasif</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const LAYER_LABELS: Record<string, string> = {
  core: 'Inti',
  distribution: 'Distribusi',
  access: 'Akses',
  passive: 'Pasif',
  customer: 'Pelanggan',
};

// ------------------------------------------------------------------ splitters

function SplittersTab() {
  const master = useMaster();
  const groups: Array<['main' | 'odp', string]> = [
    ['main', 'Main splitter (ODC)'],
    ['odp', 'Splitter ODP'],
  ];
  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', alignItems: 'start' }}
    >
      {groups.map(([cat, title]) => (
        <section key={cat} className="fn-card" style={{ overflow: 'hidden' }}>
          <div className="fn-card__header">
            <div>
              <h3 className="fn-card__title">{title}</h3>
              <p className="fn-card__desc">
                Redaman sisip dipakai di trace pelanggan dan estimasi loss budget.
              </p>
            </div>
          </div>
          <table className="fn-table">
            <thead>
              <tr>
                <th>Rasio</th>
                <th className="is-end">Redaman (dB)</th>
              </tr>
            </thead>
            <tbody>
              {master.data?.splitters
                .filter((s) => s.category === cat)
                .map((s) => (
                  <SplitterRow key={s.id} s={s} />
                ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}

function SplitterRow({ s }: { s: SplitterView }) {
  const save = useSaveMaster();
  const toast = useToast();
  const [value, setValue] = useState(String(s.lossDb));
  const [busy, setBusy] = useState(false);
  useEffect(() => setValue(String(s.lossDb)), [s.lossDb]);
  const parsed = Number(value.replace(',', '.'));
  const dirty = value !== String(s.lossDb) && Number.isFinite(parsed);
  return (
    <tr>
      <td className="mono">{s.ratio}</td>
      <td className="is-end">
        <div className="flex items-center justify-end gap-2">
          <input
            className="fn-input"
            inputMode="decimal"
            aria-label={`Redaman ${s.ratio}`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={{ width: 90, textAlign: 'right' }}
          />
          <Button
            size="sm"
            variant={dirty ? 'primary' : 'ghost'}
            disabled={!dirty}
            loading={busy}
            icon={<Check className="i" aria-hidden />}
            aria-label={`Simpan redaman ${s.ratio}`}
            onClick={() => {
              setBusy(true);
              void send<MasterData>('PUT', `/api/v1/master/splitters/${s.id}`, { lossDb: parsed })
                .then((d) => {
                  save(d);
                  toast({ tone: 'success', text: `Redaman splitter ${s.ratio} = ${nf1.format(parsed)} dB.` });
                })
                .catch((e: Error) => toast({ tone: 'danger', text: e.message }))
                .finally(() => setBusy(false));
            }}
          />
        </div>
      </td>
    </tr>
  );
}

// ------------------------------------------------------------------ fibre colours

function ColorsTab() {
  const master = useMaster();
  const save = useSaveMaster();
  const toast = useToast();
  const [editing, setEditing] = useState<FiberColorView | 'new' | null>(null);
  return (
    <section className="fn-card">
      <div className="fn-card__header">
        <div>
          <h3 className="fn-card__title">Warna tube / core</h3>
          <p className="fn-card__desc">
            Urutan standar TIA-598. Warna ke-13 dan seterusnya dipakai kabel dengan lebih dari 12 core per
            tube.
          </p>
        </div>
        <Button size="sm" icon={<Plus className="i" aria-hidden />} onClick={() => setEditing('new')}>
          Tambah warna
        </Button>
      </div>
      <div
        className="fn-card__body grid gap-2"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}
      >
        {master.data?.fiberColors.map((c) => (
          <button
            key={c.order}
            type="button"
            className="fn-layer"
            onClick={() => setEditing(c)}
            style={{ border: '1px solid var(--border-subtle)', borderRadius: 10 }}
          >
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                background: c.hex,
                border: '1px solid var(--border-muted)',
                flex: 'none',
              }}
            />
            <span className="fn-layer__name">
              {c.order}. {c.name}
            </span>
            <span className="mono fn-hint">{c.hex}</span>
          </button>
        ))}
      </div>
      {editing && (
        <FormDialogColor
          color={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(d, name) => {
            save(d);
            toast({ tone: 'success', text: `Warna ${name} tersimpan.` });
          }}
        />
      )}
    </section>
  );
}

function FormDialogColor({
  color,
  onSaved,
  onClose,
}: {
  color: FiberColorView | null;
  onSaved: (d: MasterData, name: string) => void;
  onClose: () => void;
}) {
  const [v, setV] = useState({ name: color?.name ?? '', hex: color?.hex ?? '#888888' });
  return (
    <FormDialog
      title={color ? `Ubah warna ${color.order}` : 'Tambah warna'}
      width={380}
      onClose={onClose}
      onSubmit={async () =>
        onSaved(
          color
            ? await send<MasterData>('PUT', `/api/v1/master/fiber-colors/${color.order}`, v)
            : await send<MasterData>('POST', '/api/v1/master/fiber-colors', v),
          v.name,
        )
      }
    >
      {(fe) => (
        <div className="fn-form-grid">
          <Field label="Nama" required error={fe.name}>
            <input
              className="fn-input"
              value={v.name}
              onChange={(e) => setV({ ...v, name: e.target.value })}
              required
            />
          </Field>
          <Field label="Warna" error={fe.hex}>
            <input
              className="fn-input"
              type="color"
              value={v.hex}
              onChange={(e) => setV({ ...v, hex: e.target.value })}
              style={{ height: 38, padding: 4 }}
            />
          </Field>
        </div>
      )}
    </FormDialog>
  );
}

// ------------------------------------------------------------------ cable types

function CablesTab() {
  const master = useMaster();
  const save = useSaveMaster();
  const toast = useToast();
  const [editing, setEditing] = useState<CableTypeView | 'new' | null>(null);
  const [removing, setRemoving] = useState<CableTypeView | null>(null);
  return (
    <section className="fn-card" style={{ overflow: 'hidden' }}>
      <div className="fn-card__header">
        <div>
          <h3 className="fn-card__title">Tipe kabel</h3>
          <p className="fn-card__desc">
            Harga per meter dipakai di biaya jalur dan BOQ; kosong = harga kabel default di halaman Biaya.
          </p>
        </div>
        <Button
          size="sm"
          variant="primary"
          icon={<Plus className="i" aria-hidden />}
          onClick={() => setEditing('new')}
        >
          Tambah tipe kabel
        </Button>
      </div>
      <div className="fn-table-wrap">
        <table className="fn-table">
          <thead>
            <tr>
              <th>Nama</th>
              <th>Kode</th>
              <th className="is-num">Redaman / km</th>
              <th className="is-num">Harga / m</th>
              <th className="is-num">Dipakai jalur</th>
              <th className="is-end">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {master.data?.cableTypes.map((c) => (
              <tr key={c.code}>
                <td>{c.name}</td>
                <td className="mono">{c.code}</td>
                <td className="is-num">{nf1.format(c.attenuationDbPerKm)} dB</td>
                <td className="is-num">
                  {c.pricePerM === null ? (
                    <span className="fn-hint">default</span>
                  ) : (
                    rupiah.format(c.pricePerM)
                  )}
                </td>
                <td className="is-num">{nf.format(c.routeCount)}</td>
                <td className="is-end">
                  <div className="fn-table__actions">
                    <button
                      type="button"
                      className="fn-icon-btn fn-icon-btn--sm"
                      aria-label={`Ubah ${c.name}`}
                      onClick={() => setEditing(c)}
                    >
                      <Pencil className="i i--sm" aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="fn-icon-btn fn-icon-btn--sm fn-icon-btn--danger"
                      aria-label={`Hapus ${c.name}`}
                      disabled={c.routeCount > 0}
                      title={c.routeCount > 0 ? 'Masih dipakai jalur' : undefined}
                      onClick={() => setRemoving(c)}
                    >
                      <Trash2 className="i i--sm" aria-hidden />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <CableDialog
          cable={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(d, name) => {
            save(d);
            toast({ tone: 'success', text: `Tipe kabel ${name} tersimpan.` });
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          title={`Hapus tipe kabel ${removing.name}?`}
          icon={<Trash2 className="i" aria-hidden />}
          confirmLabel="Hapus"
          onConfirm={async () => {
            save(
              await send<MasterData>(
                'DELETE',
                `/api/v1/master/cable-types/${encodeURIComponent(removing.code)}`,
              ),
            );
            toast({ tone: 'success', text: `Tipe kabel ${removing.name} dihapus.` });
            setRemoving(null);
          }}
          onClose={() => setRemoving(null)}
        />
      )}
    </section>
  );
}

function CableDialog({
  cable,
  onSaved,
  onClose,
}: {
  cable: CableTypeView | null;
  onSaved: (d: MasterData, name: string) => void;
  onClose: () => void;
}) {
  const [v, setV] = useState({
    code: cable?.code ?? '',
    name: cable?.name ?? '',
    att: String(cable?.attenuationDbPerKm ?? 0.35),
    price: cable?.pricePerM == null ? '' : String(cable.pricePerM),
  });
  return (
    <FormDialog
      title={cable ? `Ubah ${cable.name}` : 'Tambah tipe kabel'}
      onClose={onClose}
      onSubmit={async () => {
        const body: CableTypeInput = {
          code: v.code,
          name: v.name,
          attenuationDbPerKm: Number(v.att.replace(',', '.')),
          pricePerM: v.price.trim() === '' ? null : Number(v.price.replace(/\./g, '')),
        };
        onSaved(
          cable
            ? await send<MasterData>(
                'PUT',
                `/api/v1/master/cable-types/${encodeURIComponent(cable.code)}`,
                body,
              )
            : await send<MasterData>('POST', '/api/v1/master/cable-types', body),
          body.name,
        );
      }}
    >
      {(fe) => (
        <div className="fn-form-grid">
          <Field label="Nama" required wide error={fe.name}>
            <input
              className="fn-input"
              value={v.name}
              onChange={(e) => setV({ ...v, name: e.target.value })}
              required
            />
          </Field>
          <Field label="Kode" required error={fe.code}>
            <input
              className="fn-input fn-input--mono"
              value={v.code}
              onChange={(e) => setV({ ...v, code: e.target.value.toUpperCase() })}
              required
            />
          </Field>
          <Field label="Redaman (dB/km)" required error={fe.attenuationDbPerKm}>
            <input
              className="fn-input"
              inputMode="decimal"
              value={v.att}
              onChange={(e) => setV({ ...v, att: e.target.value })}
              required
            />
          </Field>
          <Field
            label="Harga per meter (Rp)"
            wide
            error={fe.pricePerM}
            hint="Kosongkan untuk memakai harga kabel default"
          >
            <input
              className="fn-input"
              inputMode="numeric"
              value={v.price}
              onChange={(e) => setV({ ...v, price: e.target.value })}
            />
          </Field>
        </div>
      )}
    </FormDialog>
  );
}

// ------------------------------------------------------------------ monitoring defaults

function MonitoringTab() {
  const toast = useToast();
  const qc = useQueryClient();
  const current = useQuery({
    queryKey: ['settings', 'monitoring'],
    queryFn: () => api<MonitoringDefaults>('/api/v1/settings/monitoring'),
  });
  const [v, setV] = useState<MonitoringDefaults | null>(null);
  const [busy, setBusy] = useState(false);
  const value = v ?? current.data;
  if (!value) return null;
  const set = (k: keyof MonitoringDefaults, x: string) => setV({ ...value, [k]: Number(x) });
  return (
    <section className="fn-card" style={{ maxWidth: 640 }}>
      <div className="fn-card__header">
        <div>
          <h3 className="fn-card__title">Monitoring default</h3>
          <p className="fn-card__desc">
            Berlaku untuk perangkat tanpa pengaturan sendiri (tab Monitoring di detail aset). Worker
            memakainya di pengecekan berikutnya.
          </p>
        </div>
      </div>
      <form
        className="fn-card__body fn-form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          void send<MonitoringDefaults>('PUT', '/api/v1/settings/monitoring', value)
            .then((d) => {
              qc.setQueryData(['settings', 'monitoring'], d);
              setV(null);
              toast({ tone: 'success', text: 'Monitoring default tersimpan.' });
            })
            .catch((err: Error) => toast({ tone: 'danger', text: err.message }))
            .finally(() => setBusy(false));
        }}
      >
        <Field label="Interval ping" hint="Seberapa sering perangkat di-ping">
          <select
            className="fn-input"
            value={value.intervalSec}
            onChange={(e) => set('intervalSec', e.target.value)}
          >
            {[
              [60, '1 menit'],
              [120, '2 menit'],
              [300, '5 menit'],
              [600, '10 menit'],
              [900, '15 menit'],
              [1800, '30 menit'],
              [3600, '1 jam'],
            ].map(([s, l]) => (
              <option key={s} value={s}>
                {l}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Offline setelah" hint="Gagal berturut-turut (anti-flapping)">
          <select
            className="fn-input"
            value={value.failThreshold}
            onChange={(e) => set('failThreshold', e.target.value)}
          >
            {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
              <option key={n} value={n}>
                {n}× gagal
              </option>
            ))}
          </select>
        </Field>
        <Field label="Warning bila latensi di atas (ms)">
          <input
            className="fn-input"
            type="number"
            min={1}
            max={60000}
            value={value.latencyWarnMs}
            onChange={(e) => set('latencyWarnMs', e.target.value)}
          />
        </Field>
        <Field label="Warning bila packet loss dari (%)">
          <input
            className="fn-input"
            type="number"
            min={1}
            max={100}
            value={value.lossWarnPct}
            onChange={(e) => set('lossWarnPct', e.target.value)}
          />
        </Field>
        <div className="span-2" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="primary"
            loading={busy}
            disabled={!v}
            icon={<Save className="i" aria-hidden />}
          >
            Simpan
          </Button>
        </div>
      </form>
    </section>
  );
}
